import React, { useState } from 'react'
import '@google/model-viewer'

const SliderRow = ({ label, value, onChange, min = -180, max = 180, step = 1, suffix = "°" }) => (
    <div style={{ marginBottom: '16px' }}>
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '4px',
            }}
        >
            <label
                style={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}
            >
                {label}
            </label>
            <span style={{ fontSize: '0.85rem', color: '#666' }}>{value}{suffix}</span>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            style={{ width: '100%', accentColor: '#1a73e8' }}
        />
    </div>
)

/**
 * CameraControlModal — lets admins set camera position and distance
 * for a 3D model with a live model-viewer preview.
 *
 * Props:
 * isOpen        – boolean
 * modelUrl      – URL of the .glb file
 * animationName – animation to play in preview
 * initialOrbit  – { theta: 0, phi: 75, radius: 100 }
 * onSubmit(orbit) – called with { theta, phi, radius }
 * onClose()     – called to dismiss
 */
const CameraControlModal = ({
    isOpen,
    modelUrl,
    animationName,
    initialOrbit,
    onSubmit,
    onClose,
}) => {
    // Theta: Horizontal rotation (-180 to 180)
    // Phi: Vertical rotation (0 to 180)
    // Radius: Distance from center (percentage or meters - usually % is easier for models)
    const [theta, setTheta] = useState(initialOrbit?.theta ?? 0)
    const [phi, setPhi] = useState(initialOrbit?.phi ?? 75)
    const [radius, setRadius] = useState(initialOrbit?.radius ?? 100)

    if (!isOpen) return null

    // model-viewer camera-orbit format: "theta phi radius"
    // Example: "45deg 75deg 105%"
    const cameraOrbitAttr = `${theta}deg ${phi}deg ${radius}%`

    const handleSubmit = () => {
        onSubmit({ theta, phi, radius })
    }

    const handleReset = () => {
        setTheta(0)
        setPhi(75)
        setRadius(100)
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose()
            }}
        >
            <div
                style={{
                    background: '#fff',
                    padding: '30px',
                    borderRadius: '12px',
                    width: '480px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 style={{ marginTop: 0, marginBottom: '6px' }}>
                    Set Camera View
                </h2>
                <p
                    style={{
                        color: '#666',
                        marginTop: 0,
                        marginBottom: '20px',
                        fontSize: '0.9rem',
                    }}
                >
                    Adjust how the camera views the model by default.
                </p>

                {/* Live Preview */}
                <div
                    style={{
                        width: '100%',
                        height: '280px',
                        background: '#f0f0f0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        marginBottom: '20px',
                        border: '1px solid #ddd',
                    }}
                >
                    <model-viewer
                        src={modelUrl}
                        camera-orbit={cameraOrbitAttr}
                        camera-controls="true"
                        shadow-intensity="1"
                        loading="eager"
                        animation-name={animationName || undefined}
                        autoplay={animationName ? 'true' : undefined}
                        style={{
                            width: '100%',
                            height: '100%',
                            outline: 'none',
                            backgroundColor: 'transparent',
                        }}
                    />
                </div>

                {/* Sliders */}
                <SliderRow 
                    label="Horizontal Angle (Theta)" 
                    value={theta} 
                    onChange={setTheta} 
                    min={-180} 
                    max={180} 
                />
                <SliderRow 
                    label="Vertical Angle (Phi)" 
                    value={phi} 
                    onChange={setPhi} 
                    min={0} 
                    max={180} 
                />
                <SliderRow 
                    label="Distance (Radius)" 
                    value={radius} 
                    onChange={setRadius} 
                    min={20} 
                    max={500} 
                    suffix="%"
                />

                {/* Actions */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '20px',
                    }}
                >
                    <button
                        onClick={handleReset}
                        style={{
                            padding: '8px 16px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            background: '#fff',
                            color: '#555',
                        }}
                    >
                        Reset Camera
                    </button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                background: '#eee',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            style={{
                                padding: '8px 16px',
                                background: '#1a73e8',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                            }}
                        >
                            Save View
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CameraControlModal